import { put, takeEvery, select } from "redux-saga/effects";
import {
  NMAxiosInst,
  SagaRegistry,
  setAlertAndLoading,
  requestHandler,
} from "@neumetric/blueprint-lib/store";
import { mitigatorActions } from "./slice";
import {
  getRisksList,
  getThreatsByStatus,
  getVulnerabilitiesByStatus,
} from "./index";
import { getSeverityList, getDecisionList } from "./helpers";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";

// -------- error message builder ----------
function buildArchiveBlockedMessage({
  entityLabel,
  entityName,
  usedRisks = [],
  usedSourceInThreats = [],
  usedThreatTypeInThreats = [],
  usedThreatInVulnerabilities = [],
}) {
  const riskTotal = usedRisks.length;
  const threatSourceTotal = usedSourceInThreats.length;
  const threatTypeTotal = usedThreatTypeInThreats.length;
  const vulnerabilityTotal = usedThreatInVulnerabilities.length;

  const name = entityName || `this ${entityLabel}`;

  const parts = [];
  if (riskTotal > 0) parts.push(`${riskTotal} active risk(s)`);
  if (threatSourceTotal > 0)
    parts.push(`${threatSourceTotal} active threat(s)`);
  if (threatTypeTotal > 0) parts.push(`${threatTypeTotal} active threat(s)`);
  if (vulnerabilityTotal > 0)
    parts.push(`${vulnerabilityTotal} active vulnerability(s)`);

  return `Cannot archive "${name}" because it is used by ${parts.join(" & ")}.`;
}

// -------- block archive for interconnected objects ----------
function* blockArchiveIfUsed({
  payload,
  entityLabel,
  entityNameKey,
  riskField,
  entityIdKey = "id",
}) {
  if (
    String(payload?.status) !== "archive" &&
    String(payload?.status) !== "archived"
  )
    return false;

  const activeRisks =
    (yield select((state) => getRisksList("active", state))) || [];

  const entityId = payload?.[entityIdKey];

  const usedRisks = activeRisks.filter(
    (r) =>
      String(r?.status) === "active" &&
      String(r?.[riskField]) === String(entityId),
  );

  let usedSourceInThreats = [];
  let usedThreatTypeInThreats = [];
  let usedThreatInVulnerabilities = [];

  // Extra check only for Source & Threat type: used in active threats
  if (entityLabel === "Source" || entityLabel === "Threat Type") {
    yield* fetchThreats();

    const activeThreats =
      (yield select((state) => getThreatsByStatus("active", state))) || [];

    usedSourceInThreats = activeThreats?.filter(
      (t) => String(t?.["source_id"]) === String(entityId),
    );
    usedThreatTypeInThreats = activeThreats?.filter(
      (t) => String(t?.["threat_type_id"]) === String(entityId),
    );
  }
  // Extra check only for Threat: used in active vulnerabilities
  if (entityLabel === "Threat") {
    yield* fetchVulnerabilities();

    const activeVulnerabilities =
      (yield select((state) => getVulnerabilitiesByStatus("active", state))) ||
      [];

    usedThreatInVulnerabilities = activeVulnerabilities.filter(
      (v) => String(v?.["threat_id"]) === String(entityId),
    );
  }
  if (
    usedRisks.length > 0 ||
    usedSourceInThreats.length > 0 ||
    usedThreatTypeInThreats.length > 0 ||
    usedThreatInVulnerabilities.length > 0
  ) {
    const msg = buildArchiveBlockedMessage({
      entityLabel,
      entityName: payload?.[entityNameKey],
      usedRisks,
      usedSourceInThreats,
      usedThreatTypeInThreats,
      usedThreatInVulnerabilities,
    });

    yield setAlertAndLoading(null, { message: msg, severity: "ERROR" });
    return true;
  }

  return false;
}

const mapRisks = (iRisk, iOptions) => {
  const { decisionList, severityList } = iOptions;
  const risk = { ...iRisk, rating: {} };
  risk.severity = risk.severity || "Minimal";
  const risk_severity =
    severityList.find((s) => s.id === risk.severity || s.label === risk.severity) || null;
  const decision = decisionList.find((d) => d.id === risk.decision) || null;
  risk.impact = risk.impact !== null ? Number(risk.impact) : -1;
  risk.score = risk.score !== null ? Number(risk.score) : -1;
  risk.probability = risk.probability !== null ? Number(risk.probability) : -1;
  const ratingFields = [
    "confidentiality",
    "integrity",
    "availability",
    "security",
    "privacy",
  ];
  ratingFields.forEach((field) => {
    risk.rating[field] =
      risk[field] === null ? -1 : Number(risk[field]) > -1 ? risk[field] : -1;
  });
  if (risk_severity) {
    risk.risk_severity_item = { ...risk_severity };
  }
  if (decision) {
    risk.decision_item = {
      label: decision.label,
      id: decision.dotIconName || decision.id,
    };
  }
  return risk;
};

export function convertToCSV(headers, rows) {
  let csv = headers.join(",") + "\n";
  rows.forEach((row) => {
    csv += headers.map((h) => JSON.stringify(row[h] || "")).join(",") + "\n";
  });
  return csv;
}

export function* handleFileImport({ apiPath, file, fetchAction }) {
  const formData = new FormData();
  formData.append("file", file);

  const res = yield NMAxiosInst.post(
    apiPath,
    formData,
    NMAxiosInst.getFormDataContentType(),
  );

  const data = res;

  const csv = convertToCSV(data.headers, data.rows);
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const popupResult = {
    success: data.success,
    failure: data.failure,
    filename: data.filename,
    message: data.message,

    onDownloadResult: () => {
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
    },
  };
  yield put(mitigatorActions.setImportResult(popupResult));
  if (fetchAction) {
    yield put(fetchAction());
  }
}

function* fetchSources() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/sources_index");
    yield put(mitigatorActions.setSourcesList(res));
  }, "Failed to fetch sources");
}

function* fetchThreatTypes() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/index_threat_types");
    yield put(mitigatorActions.setThreatTypesList(res));
  }, "Failed to fetch threat types");
}

function* fetchThreats() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/index_threats");
    const list = Array.isArray(res) ? res : [];
    const transformed = list.map((content) => ({
      ...content,
      source: content?.source?.name || null,
      threat_type: content?.threat_type?.threat_type || null,
    }));
    yield put(mitigatorActions.setThreatsList(transformed));
  }, "Failed to fetch threats");
}

function* fetchVulnerabilities() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/index_vulnerabilities");
    const list = Array.isArray(res) ? res : [];
    const transformed = list.map((content) => ({
      ...content,
      threat: content?.threat?.threat || null,
    }));
    yield put(mitigatorActions.setVulnerabilitiesList(transformed));
  }, "Failed to fetch Vulnerabilities");
}

function* fetchAssetList() {
  yield requestHandler(function* () {
    let res = yield NMAxiosInst.get("/risks/index_assets");
    const list = Array.isArray(res) ? res : [];
    const transformed = list.map((content) => ({
      ...content,
      asset_type: content?.asset_type?.asset_type || null,
    }));
    const sorted = transformed.slice().sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
    yield put(mitigatorActions.setAssetsList(sorted));
  }, "Failed to fetch Assets");
}
function* fetchAssetTypeList() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/asset_types_index");
    const list = Array.isArray(res) ? res : [];
    const sorted = list.slice().sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
    yield put(mitigatorActions.setAssetTypesList(sorted));
  }, "Failed to fetch Asset Types");
}
const RATING_TYPES = [
  "confidentiality",
  "integrity",
  "availability",
  "security",
  "privacy",
  "impact",
  "acceptance",
];

const groupRatingsByType = (list = []) => {
  const grouped = RATING_TYPES.reduce((acc, k) => {
    acc[k] = [];
    return acc;
  }, {});

  // filter out wrong-seeded rows (rating null/empty)
  const cleanList = (list || []).filter(
    (item) =>
      item &&
      item.rating_type &&
      item.rating != null && // removes null/undefined
      String(item.rating).trim() !== "", // removes ""
  );

  for (const item of cleanList) {
    const key = String(item.rating_type || "").toLowerCase();
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }

  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));
  }

  return grouped;
};

function* fetchRatings() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/ratings");
    const grouped = groupRatingsByType(res);
    yield put(mitigatorActions.setRatingsList(grouped));
  }, "Failed to fetch Ratings");
}

const getRatingText = (row) =>
  row?.rating?._id || row?.rating?.label || row?.rating || "";

function* updateRatings(action) {
  const options = {
    errorMessage: "Failed to update Ratings",
    action,
  };

  yield requestHandler(function* () {
    const payload = action.payload || {};
    const { rows = [] } = payload;

    for (const r of rows) {
      const body = {
        rating: {
          rating: getRatingText(r),
          score: r?.score ?? null,
          description: r?.description ?? "",
          creator_id: r?.creator_id ?? 1,
          updater_id: r?.updater_id ?? 1,
        },
      };

      yield NMAxiosInst.put(`/risks/${r.id}/update_rating`, body);
    }

    yield put(mitigatorActions.fetchRatings());

    yield setAlertAndLoading(null, {
      message: "Ratings updated successfully.",
    });
  }, options);
}

function* fetchReferences() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks/references_index");
    yield put(mitigatorActions.setReferencesList(res));
  }, "Failed to fetch References");
}

function* createSource(action) {
  const options = {
    errorMessage: "Failed to create source",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const body = {
      source: {
        name: payload?.name,
        description: payload?.description ?? "",
      },
    };
    const res = yield NMAxiosInst.post("/risks/sources_create", body);
    yield put(mitigatorActions.updateSourceInList(res));

    yield setAlertAndLoading(null, { message: "Source created successfully." });
  }, options);
}

export function* updateSource(action) {
  const options = {
    errorMessage: "Failed to update source",
    action: action,
  };

  yield requestHandler(function* () {
    const payload = action.payload;
    const { id } = payload;

    const blocked = yield* blockArchiveIfUsed({
      payload,
      entityLabel: "Source",
      entityNameKey: "name",
      riskField: "source_id",
      entityIdKey: "id",
    });

    if (blocked) return;

    const body = {
      source: {
        name: payload?.name,
        description: payload?.description ?? "",
        status: payload?.status,
      },
    };

    const res = yield NMAxiosInst.put(`/risks/${id}/sources_update`, body);
    yield put(mitigatorActions.updateSourceInList(res));
    yield setAlertAndLoading(null, { message: "Source updated successfully." });
  }, options);
}

function* importSource(action) {
  const options = {
    errorMessage: "Failed to import source",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_sources",
      file,
      fetchAction: mitigatorActions.fetchSources,
    });
  }, options);
}

function* createThreatType(action) {
  const options = {
    errorMessage: "Failed to create Threat Type",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const body = {
      threat_type: {
        threat_type_id: payload?.threat_type_id,
        threat_type: payload?.threat_type,
        status: payload?.status,
      },
    };
    const res = yield NMAxiosInst.post("/risks/create_threat_type", body);
    yield put(mitigatorActions.updateThreatTypeInList(res));
    yield setAlertAndLoading(null, {
      message: "Threat Type created successfully.",
    });
  }, options);
}
function* importThreatType(action) {
  const options = {
    errorMessage: "Failed to import threat type",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_threat_types",
      file,
      fetchAction: mitigatorActions.fetchThreatTypes,
    });
  }, options);
}

export function* updateThreatType(action) {
  const options = {
    errorMessage: "Failed to update Threat Type",
    action: action,
  };

  yield requestHandler(function* () {
    const payload = action.payload;
    const { id } = payload;

    const blocked = yield* blockArchiveIfUsed({
      payload,
      entityLabel: "Threat Type",
      entityNameKey: "threat_type",
      riskField: "threat_type_id",
      entityIdKey: "id",
    });

    if (blocked) return;

    const body = {
      threat_type: {
        threat_type: payload?.threat_type,
        status: payload?.status,
      },
    };

    const res = yield NMAxiosInst.put(`/risks/${id}/update_threat_type`, body);
    yield put(mitigatorActions.updateThreatTypeInList(res));
    yield setAlertAndLoading(null, {
      message: "Threat Type updated successfully.",
    });
  }, options);
}
function* createThreat(action) {
  const options = {
    errorMessage: "Failed to create Threat",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const body = {
      threat: {
        threat_type_id: payload?.threat_type_id ?? "",
        threat: payload?.threat ?? "",
        description: payload?.description ?? "",
        status: payload?.status ?? "",
        source_id: payload?.source_id ?? "",
      },
    };
    const res = yield NMAxiosInst.post("/risks/create_threat", body);
    const transformed = {
      ...res,
      source: res?.source?.name ?? "",
      threat_type: res?.threat_type?.threat_type ?? "",
    };
    yield put(mitigatorActions.updateThreatInList(transformed));
    yield setAlertAndLoading(null, { message: "Threat created successfully." });
  }, options);
}
function* updateThreat(action) {
  const options = {
    errorMessage: "Failed to update Threat",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const { id } = payload;

    const blocked = yield* blockArchiveIfUsed({
      payload,
      entityLabel: "Threat",
      entityNameKey: "threat",
      riskField: "threat_id",
      entityIdKey: "id",
    });

    if (blocked) return;

    const body = {
      threat: {
        threat_id: payload?.threat_id ?? "",
        threat: payload?.threat ?? "",
        description: payload?.description ?? "",
        status: payload?.status ?? "",
        source_id: payload?.source_id ?? "",
        threat_type_id: payload?.threat_type_id ?? "",
      },
    };
    const res = yield NMAxiosInst.put(`/risks/${id}/update_threat`, body);
    const transformed = {
      ...res,
      source: res?.source?.name || null,
      threat_type: res?.threat_type?.threat_type || null,
    };
    yield put(mitigatorActions.updateThreatInList(transformed));
    yield setAlertAndLoading(null, { message: "Threat updated successfully." });
  }, options);
}
function* importThreat(action) {
  const options = {
    errorMessage: "Failed to import threat",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_threats",
      file,
      fetchAction: mitigatorActions.fetchThreats,
    });
  }, options);
}

function* createVulnerability(action) {
  const options = {
    errorMessage: "Failed to create Vulnerability",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const body = {
      vulnerability: {
        vulnerability_id: payload?.vulnerability_id,
        vulnerability: payload?.vulnerability ?? "",
        category: payload?.category ?? "",
        threat_id: payload?.threat_id ?? "",
        status: payload?.status ?? "",
      },
    };
    const res = yield NMAxiosInst.post("/risks/create_vulnerability", body);
    const transformed = {
      ...res,
      threat: res?.threat?.threat || null,
    };
    yield put(mitigatorActions.updateVulnerabilityInList(transformed));
    yield setAlertAndLoading(null, {
      message: "Vulnerability created successfully.",
    });
  }, options);
}
function* updateVulnerability(action) {
  const options = {
    errorMessage: "Failed to update Vulnerability",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const { id } = payload;
    const blocked = yield* blockArchiveIfUsed({
      payload,
      entityLabel: "Vulnerability",
      entityNameKey: "vulnerability",
      riskField: "vulnerability_id",
      entityIdKey: "id",
    });

    if (blocked) return;
    const body = {
      vulnerability: {
        vulnerability: payload?.vulnerability ?? "",
        category: payload?.category ?? "",
        threat_id: payload?.threat_id ?? "",
        status: payload?.status ?? "",
      },
    };
    const res = yield NMAxiosInst.put(
      `/risks/${id}/update_vulnerability`,
      body,
    );
    const transformed = {
      ...res,
      threat: res?.threat?.threat || null,
    };
    yield put(mitigatorActions.updateVulnerabilityInList(transformed));
    yield setAlertAndLoading(null, {
      message: "Vulnerability updated successfully.",
    });
  }, options);
}
function* importVulnerability(action) {
  const options = {
    errorMessage: "Failed to import vulnerability",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_vulnerabilities",
      file,
      fetchAction: mitigatorActions.fetchVulnerabilities,
    });
  }, options);
}

function* createAssetType(action) {
  const options = {
    errorMessage: "Failed to create asset type",
    action: action,
  };
  yield requestHandler(function* () {
    const assetType = action.payload.type;
    const body = {
      asset_type: {
        asset_type_id: "AST-001",
        asset_type: assetType,
      },
    };
    yield NMAxiosInst.post("/risks/asset_types_create", body);
    yield put(mitigatorActions.fetchAssetTypeList());
    yield setAlertAndLoading(null, {
      message: "Asset type created successfully.",
    });
  }, options);
}
function* updateAssetType(action) {
  const options = {
    errorMessage: "Failed to update asset type",
    action: action,
  };
  yield requestHandler(function* () {
    const { type, id } = action.payload;
    const body = {
      asset_type: {
        asset_type: type,
      },
    };
    const res = yield NMAxiosInst.put(`/risks/${id}/asset_types_update`, body);
    yield put(mitigatorActions.updateAssetTypeInList(res));
    yield setAlertAndLoading(null, {
      message: "Asset type updated successfully.",
    });
  }, options);
}

function* importAssetType(action) {
  const options = {
    errorMessage: "Failed to import asset type",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_asset_types",
      file,
      fetchAction: mitigatorActions.fetchAssetTypeList,
    });
  }, options);
}
function* createAsset(action) {
  const options = {
    errorMessage: "Failed to create asset",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const body = {
      asset: {
        asset: payload.asset ?? "",
        asset_id: payload.asset_id ?? "",
        asset_type_id: payload.asset_type_id ?? "",
        status: payload.status ?? "",
      },
    };
    yield NMAxiosInst.post("/risks/create_asset", body);
    yield put(mitigatorActions.fetchAssetList());
    yield setAlertAndLoading(null, {
      message: "Asset created successfully.",
    });
  }, options);
}
function* updateAsset(action) {
  const options = {
    errorMessage: "Failed to update asset",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const { id } = payload;
    const body = {
      asset: {
        asset: payload.asset ?? "",
        asset_type_id: payload.asset_type_id ?? "",
        status: payload.status ?? "",
      },
    };
    const res = yield NMAxiosInst.put(`/risks/${id}/update_asset`, body);
    const transformed = {
      ...res,
      asset_type: res?.asset_type?.asset_type || null,
    };
    yield put(mitigatorActions.updateAssetInList(transformed));
    yield setAlertAndLoading(null, {
      message: "Asset updated successfully.",
    });
  }, options);
}
function* importAsset(action) {
  const options = {
    errorMessage: "Failed to import asset",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_assets",
      file,
      fetchAction: mitigatorActions.fetchAssetList,
    });
  }, options);
}

function* createReference(action) {
  const options = {
    errorMessage: "Failed to create Reference",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const body = {
      reference: {
        link: payload.link ?? "",
        name: payload.name ?? "",
        status: payload.status ?? "",
      },
    };
    const res = yield NMAxiosInst.post("/risks/reference_create", body);
    yield put(mitigatorActions.updateReferenceInList());
    yield setAlertAndLoading(null, {
      message: "Reference created successfully.",
    });
  }, options);
}
function* updateReference(action) {
  const options = {
    errorMessage: "Failed to update Reference",
    action: action,
  };
  yield requestHandler(function* () {
    const payload = action.payload;
    const { id } = payload;
    const body = {
      reference: {
        link: payload.link ?? "",
        name: payload.name ?? "",
        status: payload.status ?? "",
      },
    };
    const res = yield NMAxiosInst.put(`/risks/${id}/reference_update`, body);
    yield put(mitigatorActions.updateReferenceInList(res));
    yield setAlertAndLoading(null, {
      message: "Reference updated successfully.",
    });
  }, options);
}
function* importReference(action) {
  const options = {
    errorMessage: "Failed to import reference",
    action,
  };

  yield requestHandler(function* () {
    const { file } = action.payload;

    yield* handleFileImport({
      apiPath: "/risks/import_references",
      file,
      fetchAction: mitigatorActions.fetchReferences,
    });
  }, options);
}

function* fetchSource({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/risks/${payload.source_id}/sources_show`,
    );
    yield put(mitigatorActions.setSource(res));
  }, "Failed to fetch the source");
}
function* fetchThreatType({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/risks/${payload.threat_type_id}/show_threat_type`,
    );
    yield put(mitigatorActions.setThreatType(res));
  }, "Failed to fetch the threat type");
}
function* fetchThreat({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/risks/${payload.threat_id}/show_threat`,
    );
    const transformed = {
      ...res,
      source: res.source?.name ?? "",
      threat_type: res.threat_type?.threat_type ?? "",
    };
    yield put(mitigatorActions.setThreat(transformed));
  }, "Failed to fetch the threat");
}
function* fetchVulnerability({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/risks/${payload.vulnerability_id}/show_vulnerability`,
    );
    const transformed = {
      ...res,
      threat: res.threat?.threat ?? "",
    };
    yield put(mitigatorActions.setVulnerability(transformed));
  }, "Failed to fetch the vulnerability");
}
function* fetchAsset({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(`/risks/${payload.asset_id}/show_asset`);
    const transformed = {
      ...res,
      asset_type: res.asset_type
        ? {
            id: res.asset_type.id,
            label: res.asset_type.asset_type,
          }
        : "",
    };
    yield put(mitigatorActions.setAsset(transformed));
  }, "Failed to fetch the asset");
}
function* fetchAssetType({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/risks/${payload.asset_type_id}/asset_types_show`,
    );
    yield put(mitigatorActions.setAssetType(res));
  }, "Failed to fetch the asset type");
}
function* fetchReference({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(
      `/risks/${payload.reference_id}/reference_show`,
    );
    yield put(mitigatorActions.setReference(res));
  }, "Failed to fetch the reference");
}
function* createRisk(action) {
  const options = {
    errorMessage: "Failed to create Risk",
    action: action,
  };
  yield requestHandler(function* () {
    const body = action.payload;
    yield NMAxiosInst.post("/risks", body);
    yield setAlertAndLoading(null, { message: "Risk created successfully." });
  }, options);
}
function* fetchRisks() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/risks");
    const severityList = yield select(getSeverityList);
    const decisionList = yield select(getDecisionList);
    const mappedRisks = res.map((risk) =>
      mapRisks(risk, { severityList, decisionList }),
    );
    mappedRisks.sort(DateTimeUtil.sorterCB.bind(null, "created_at"));
    yield put(mitigatorActions.setRisksList(mappedRisks));
  }, "Failed to fetch risks");
}
function* fetchRisk({ payload }) {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(`/risks/${payload.risk_id}`);
    const decisionList = yield select(getDecisionList);
    const severityList = yield select(getSeverityList);
    const mappedRisk = mapRisks(res, { severityList, decisionList });
    yield put(mitigatorActions.setCurrentRisk(mappedRisk));
    return mappedRisk;
  }, "Failed to fetch the risk");
}
function* updateRisk(action) {
  const options = {
    errorMessage: "Failed to update Risk",
    action: action,
  };
  yield requestHandler(function* () {
    const risk = action.payload;
    yield NMAxiosInst.put(`/risks/${risk.guid}`, { risk });
    const res = yield put(mitigatorActions.fetchRisk({ risk_id: risk.guid }));
    yield setAlertAndLoading(null, { message: "Risk updated successfully." });
    return res;
  }, options);
}
function* createRiskControl(action) {
  const options = {
    errorMessage: "Failed to create Risk Control",
    action: action,
  };
  yield requestHandler(function* () {
    const { control, risk_id } = action.payload;
    const body = {
      risk_control: {
        title: control.title,
        priority: control.priority,
      },
      id: risk_id,
    };
    yield NMAxiosInst.post(`/risks/create_risk_control`, body);
    yield put(mitigatorActions.fetchRisk({ risk_id: risk_id }));
    yield setAlertAndLoading(null, {
      message: "Risk Control created successfully.",
    });
  }, options);
}
function* deleteRiskControl(action) {
  const options = {
    errorMessage: "Failed to delete Risk Control",
    action: action,
  };
  yield requestHandler(function* () {
    const { control_id, risk_id } = action.payload;
    yield NMAxiosInst.delete(`/risks/${control_id}/destroy_risk_control`);
    yield put(mitigatorActions.fetchRisk({ risk_id: risk_id }));
    yield setAlertAndLoading(null, {
      message: "Risk Control deleted successfully.",
    });
  }, options);
}
function* importRisks(action) {
  const options = {
    errorMessage: "Failed to import risks",
    action,
  };
  yield requestHandler(function* () {
    const { file } = action.payload;
    yield handleFileImport({
      apiPath: "/risks/upload_risks",
      file,
      fetchAction: mitigatorActions.fetchRisks,
    });
  }, options);
}
// function* bulkUpdateThreatStatus(action) {
//   const options = {
//     errorMessage: "Failed to update threats",
//     action: action,
//   };

//   yield requestHandler(function* () {
//     const { threat_ids, updates } = action.payload;

//     const body = {
//       threat_ids,
//       updates,
//     };
//     const res = yield NMAxiosInst.put(`/risks/bulk_update_threat_status`, body);

//     yield put(mitigatorActions.fetchThreats());

//     yield setAlertAndLoading(null, {
//       message: "Threat statuses updated successfully.",
//     });
//   }, options);
// }
// function* bulkUpdateVulnerabilityStatus(action) {
//   const options = {
//     errorMessage: "Failed to update vulnerabilities",
//     action,
//   };

//   yield requestHandler(function* () {
//     const { vulnerability_ids, updates } = action.payload;

//     const body = {
//       vulnerability_ids,
//       updates,
//     };

//     const res = yield NMAxiosInst.put(`/risks/bulk_update_vul_status`, body);

//     yield put(mitigatorActions.fetchVulnerabilities());

//     yield setAlertAndLoading(null, {
//       message: "Vulnerabilities updated successfully.",
//     });
//   }, options);
// }

const isBulkArchiving = (updates) => {
  const st = String(updates?.status || "").toLowerCase();
  return st === "archive" || st === "archived";
};

function* bulkUpdateThreatStatus(action) {
  const options = {
    errorMessage: "Failed to update threats",
    action: action,
  };

  yield requestHandler(function* () {
    const { threat_ids = [], updates } = action.payload;

    // guard for bulk-archive
    if (isBulkArchiving(updates)) {
      const activeRisks =
        (yield select((state) => getRisksList("active", state))) || [];

      const activeVulnerabilities =
        (yield select((state) =>
          getVulnerabilitiesByStatus("active", state),
        )) || [];

      // threats used in active risks
      const usedThreatIdsInRisks = new Set(
        activeRisks
          .filter((r) => String(r?.status) === "active")
          .map((r) => r?.threat_id)
          .filter((v) => v !== null && v !== undefined)
          .map((v) => String(v)),
      );

      // threats used in active vulnerabilities
      const usedThreatIdsInVuls = new Set(
        activeVulnerabilities
          .filter((v) => String(v?.status) === "active")
          .map((v) => v?.threat_id)
          .filter((x) => x !== null && x !== undefined)
          .map((x) => String(x)),
      );

      const blockedIds = [];
      const allowedIds = [];

      threat_ids.forEach((id) => {
        const key = String(id);
        if (usedThreatIdsInRisks.has(key) || usedThreatIdsInVuls.has(key)) {
          blockedIds.push(id);
        } else {
          allowedIds.push(id);
        }
      });

      // all blocked -> stop
      if (allowedIds.length === 0) {
        yield setAlertAndLoading(null, {
          severity: "ERROR",
          message: `Cannot archive selected Threat(s). They are used by active risk(s) and/or active vulnerability(s).`,
        });
        return;
      }

      // archive only allowed ids
      const body = { threat_ids: allowedIds, updates };
      yield NMAxiosInst.put(`/risks/bulk_update_threat_status`, body);

      yield put(mitigatorActions.fetchThreats());

      yield setAlertAndLoading(null, {
        severity: blockedIds.length > 0 ? "ERROR" : "SUCCESS",
        message:
          blockedIds.length > 0
            ? `Archived ${allowedIds.length} Threat(s). Skipped ${blockedIds.length} because they are used by active risk(s) and/or active vulnerability(s).`
            : "Threat statuses updated successfully.",
      });

      return;
    }

    // normal bulk update (not archive)
    const body = { threat_ids, updates };
    yield NMAxiosInst.put(`/risks/bulk_update_threat_status`, body);
    yield put(mitigatorActions.fetchThreats());
    yield setAlertAndLoading(null, {
      severity: "SUCCESS",
      message: "Threat statuses updated successfully.",
    });
  }, options);
}

function* bulkUpdateVulnerabilityStatus(action) {
  const options = {
    errorMessage: "Failed to update vulnerabilities",
    action,
  };

  yield requestHandler(function* () {
    const { vulnerability_ids = [], updates } = action.payload;

    //  guard for bulk-archive
    if (isBulkArchiving(updates)) {
      const activeRisks =
        (yield select((state) => getRisksList("active", state))) || [];

      const usedVulnIdsInRisks = new Set(
        activeRisks
          .filter((r) => String(r?.status) === "active")
          .map((r) => r?.vulnerability_id)
          .filter((v) => v !== null && v !== undefined)
          .map((v) => String(v)),
      );

      const blockedIds = [];
      const allowedIds = [];

      vulnerability_ids.forEach((id) => {
        if (usedVulnIdsInRisks.has(String(id))) blockedIds.push(id);
        else allowedIds.push(id);
      });

      if (allowedIds.length === 0) {
        yield setAlertAndLoading(null, {
          severity: "ERROR",
          message: `Cannot archive selected Vulnerability(s). They are used by active risk(s).`,
        });
        return;
      }

      const body = { vulnerability_ids: allowedIds, updates };
      yield NMAxiosInst.put(`/risks/bulk_update_vul_status`, body);

      yield put(mitigatorActions.fetchVulnerabilities());

      yield setAlertAndLoading(null, {
        severity: blockedIds.length > 0 ? "ERROR" : "SUCCESS",
        message:
          blockedIds.length > 0
            ? `Archived ${allowedIds.length} Vulnerability(s). Skipped ${blockedIds.length} because they are used by active risk(s).`
            : "Vulnerabilities updated successfully.",
      });

      return;
    }

    // normal bulk update (not archive)
    const body = { vulnerability_ids, updates };
    yield NMAxiosInst.put(`/risks/bulk_update_vul_status`, body);
    yield put(mitigatorActions.fetchVulnerabilities());
    yield setAlertAndLoading(null, {
      severity: "SUCCESS",
      message: "Vulnerabilities updated successfully.",
    });
  }, options);
}

SagaRegistry.register(function* mitigatorSaga() {
  yield takeEvery("mitigator/fetchSources", fetchSources);
  yield takeEvery("mitigator/fetchThreatTypes", fetchThreatTypes);
  yield takeEvery("mitigator/fetchThreats", fetchThreats);
  yield takeEvery("mitigator/fetchVulnerabilities", fetchVulnerabilities);
  yield takeEvery("mitigator/fetchAssetList", fetchAssetList);
  yield takeEvery("mitigator/fetchAssetTypeList", fetchAssetTypeList);
  yield takeEvery("mitigator/fetchRatings", fetchRatings);
  yield takeEvery("mitigator/fetchReferences", fetchReferences);
  yield takeEvery("mitigator/createSource", createSource);
  yield takeEvery("mitigator/importSource", importSource);
  yield takeEvery("mitigator/updateSource", updateSource);
  yield takeEvery("mitigator/createThreatType", createThreatType);
  yield takeEvery("mitigator/importThreatType", importThreatType);
  yield takeEvery("mitigator/updateThreatType", updateThreatType);
  yield takeEvery("mitigator/createThreat", createThreat);
  yield takeEvery("mitigator/importThreat", importThreat);
  yield takeEvery("mitigator/updateThreat", updateThreat);
  yield takeEvery("mitigator/createVulnerability", createVulnerability);
  yield takeEvery("mitigator/importVulnerability", importVulnerability);
  yield takeEvery("mitigator/updateVulnerability", updateVulnerability);
  yield takeEvery("mitigator/createAssetType", createAssetType);
  yield takeEvery("mitigator/importAssetType", importAssetType);
  yield takeEvery("mitigator/updateAssetType", updateAssetType);
  yield takeEvery("mitigator/createAsset", createAsset);
  yield takeEvery("mitigator/importAsset", importAsset);
  yield takeEvery("mitigator/updateAsset", updateAsset);
  yield takeEvery("mitigator/createReference", createReference);
  yield takeEvery("mitigator/importReference", importReference);
  yield takeEvery("mitigator/updateReference", updateReference);
  yield takeEvery("mitigator/fetchSource", fetchSource);
  yield takeEvery("mitigator/fetchThreatType", fetchThreatType);
  yield takeEvery("mitigator/fetchThreat", fetchThreat);
  yield takeEvery("mitigator/fetchVulnerability", fetchVulnerability);
  yield takeEvery("mitigator/fetchAsset", fetchAsset);
  yield takeEvery("mitigator/fetchAssetType", fetchAssetType);
  yield takeEvery("mitigator/fetchReference", fetchReference);
  yield takeEvery("mitigator/createRisk", createRisk);
  yield takeEvery("mitigator/fetchRisks", fetchRisks);
  yield takeEvery("mitigator/fetchRisk", fetchRisk);
  yield takeEvery("mitigator/updateRisk", updateRisk);
  yield takeEvery("mitigator/createRiskControl", createRiskControl);
  yield takeEvery("mitigator/deleteRiskControl", deleteRiskControl);
  yield takeEvery("mitigator/importRisks", importRisks);
  yield takeEvery("mitigator/bulkUpdateThreatStatus", bulkUpdateThreatStatus);
  yield takeEvery(
    "mitigator/bulkUpdateVulnerabilityStatus",
    bulkUpdateVulnerabilityStatus,
  );
  yield takeEvery("mitigator/updateRatings", updateRatings);
});
